import { initializeApp } from 'firebase/app'
import {
  GoogleAuthProvider,
  connectAuthEmulator,
  getAuth,
  setPersistence,
  browserLocalPersistence,
} from 'firebase/auth'
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore'

function required(name: string): string {
  const value = import.meta.env[name] as string | undefined
  if (!value) {
    throw new Error(
      `Missing ${name}. Copy apps/app/.env.example to apps/app/.env and fill it in ` +
        `from the Firebase console (Project settings -> Your apps -> Web app).`,
    )
  }
  return value
}

export const firebaseApp = initializeApp({
  apiKey: required('VITE_FIREBASE_API_KEY'),
  authDomain: required('VITE_FIREBASE_AUTH_DOMAIN'),
  projectId: required('VITE_FIREBASE_PROJECT_ID'),
  storageBucket: required('VITE_FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: required('VITE_FIREBASE_MESSAGING_SENDER_ID'),
  appId: required('VITE_FIREBASE_APP_ID'),
})

export const auth = getAuth(firebaseApp)
export const db = getFirestore(firebaseApp)

/**
 * Sessions live in origin-scoped IndexedDB. That is why app.racedash.live and
 * admin.racedash.live cannot share a session — the browser enforces the
 * separation the admin design asks for, rather than our code having to.
 *
 * Note that authDomain is the app's own origin rather than
 * <project>.firebaseapp.com, so the sign-in popup never shows a URL the user
 * doesn't recognise, and the popup is same-origin with its opener instead of
 * relying on cross-origin storage that browsers keep tightening.
 */
void setPersistence(auth, browserLocalPersistence)

export const googleProvider = new GoogleAuthProvider()

/**
 * Apple is not enabled yet — it needs a paid Apple Developer account, and there
 * is no iOS app to require it. Two things it will bring, both already handled:
 * private-relay addresses (so invites redeem by token, never by email match),
 * and a display name returned only on first sign-in (so bootstrap captures it).
 */

if (import.meta.env.VITE_USE_EMULATORS === '1') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true })
  connectFirestoreEmulator(db, '127.0.0.1', 8080)
}
